/**
 * store/index.js — configuration du store Redux (vague 4 lot 3).
 *
 * Deux évolutions par rapport au `createStore(rootReducer, applyMiddleware(thunk))`
 * historique, à comportement runtime IDENTIQUE :
 *
 * 1. HYDRATATION de la session au boot (UNE seule lecture localStorage._user).
 *    Avant, `state.user.user` démarrait à {} et chaque composant relisait
 *    localStorage.getItem('_user') à son render pour reconstruire les flags (42
 *    lectures dispersées, source des bugs « nav figée post-login » / « silent
 *    logout » quand une lecture divergeait). Désormais le store porte le user dès
 *    le premier render → selectAuthFlags (store/selectors/auth.js) est la source
 *    unique, hydratée ici. La lecture passe par userStorage (SEUL module autorisé
 *    à toucher la clé ; aucun import inverse → pas de cycle store→session→store).
 *
 * 2. createStore → configureStore (@reduxjs/toolkit, déjà présent — cartSlice /
 *    statusSlice / mapImagesSlice l'utilisent déjà via combineReducers).
 *    - thunk : inclus par défaut dans getDefaultMiddleware → STRICTE parité avec
 *      l'ancien applyMiddleware(thunk). Aucun autre middleware n'était câblé
 *      (userCartSync.js existe mais n'est PAS monté — cf. son en-tête « pas câblé
 *      au store en S1 ») : on ne l'ajoute pas (préservation §69).
 *    - serializableCheck / immutableCheck : DÉSACTIVÉS volontairement. Le state
 *      legacy contient par conception des valeurs non sérialisables, ANTÉRIEURES
 *      à ce lot et hors périmètre (les purger = gros refactor risqué) :
 *        • reducers/filters.js garde des Set en state (selectedCategories,
 *          selectedStatus, selectedItemsType, selectedCollections) ;
 *        • store/utils.js (entityLoadingStarted) stocke un canceler axios dans
 *          l'état de chargement des entités NFT.
 *      Les laisser actifs changerait le comportement (throw en dev / warn en
 *      boucle sur du state pré-existant valide) : on les coupe pour l'ISO-comportement.
 *      Le reducer user, lui, ne stocke QUE l'objet JSON du user → session 100 %
 *      sérialisable.
 *    devTools : activé hors production uniquement (défaut RTK).
 */
import { configureStore } from '@reduxjs/toolkit';
import rootReducer from './reducers';
import * as actions from './actions';
import { readStoredUser } from '../utils/auth/userStorage';

// Hydratation unique : lit la session persistée (null si absent / illisible /
// storage indispo → visiteur) pour amorcer le state initial du reducer user, de
// sorte que le tout premier render dispose déjà des flags (selectAuthFlags).
const storedUser = readStoredUser() || {};

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

// Reflète la session au boot via le MÊME chemin que toute mise à jour de session
// (actions.setUser) → le reducer user applique aussi, de façon cohérente avec
// login/logout, le scope Sentry (id / is_admin). Dispatch unique, seulement si une
// session existe (sinon on garde defaultState.user = {} = visiteur).
if (Object.keys(storedUser).length > 0) {
  store.dispatch(actions.setUser(storedUser));
}

export default store;
